import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatRadioModule } from '@angular/material/radio';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { SearchFilterSectionComponent } from '../../search-filter/search-filter-section/search-filter-section.component';
import { SearchFilterService } from 'src/inventory-management/services/search-filter/search-filter.service';
import { AccountManagerSharedVehicle } from 'src/inventory-management/interfaces/dealer-group.interface';
import { Store } from '@ngxs/store';
import { InventoryManagementState, UpdateInventoryManagementAccountManagerOfferListSelection } from 'remarketing-angular-library';

@Component({
  selector: 'app-account-manager-shared-vehicles-filter',
  imports: [FormsModule, TranslocoDirective, MatRadioModule, SearchFilterSectionComponent],
  templateUrl: './account-manager-shared-vehicles-filter.component.html',
  styleUrl: './account-manager-shared-vehicles-filter.component.scss'
})
export class AccountManagerSharedVehiclesFilterComponent extends SaComponent implements OnInit {
  private readonly searchFilterService = inject(SearchFilterService);
  private readonly store = inject(Store);

  /** Track which dealer dropdowns are open */
  dealerDropdownOpen = new Map<string, boolean>();

  /** Visible dealer count for each account manager, keyed by account manager UUID */
  protected visibleDealerCounts: Record<string, WritableSignal<number>> = {};

  /** Number of dealers to show per page */
  private readonly dealersPerPage = 10;

  /** 
   * Selected value tracking:
   * - 'all' = All Recipients selected
   * - 'accountManager:{uuid}' = All Lists for a specific account manager
   * - 'recipient:{accountManagerUuid}:{dealerUuid}:{recipientUuid}' = All Lists for a specific recipient
   * - 'offerList:{offerListUuid}' = Specific offer list selected
   */
  selectedValue: WritableSignal<string> = signal('all');

    ngOnInit(): void {
    this.setInitialSelectedVehicles();
    this.initializeVisibleDealerCounts();
  }

  private initializeVisibleDealerCounts(): void {
    this.accountManagersForOfferLists.forEach(accountManager => {
      this.visibleDealerCounts[accountManager.value] = signal(this.dealersPerPage);
    });
  }

  get accountManagersForOfferLists(): AccountManagerSharedVehicle[] {
    const rawData = this.searchFilterService.getAccountManagerEmployeesForOfferLists();
    if (!rawData || rawData.length === 0) {
      return [];
    }
    return rawData as AccountManagerSharedVehicle[];
  }

  // set initial values if nav-ing back from sell VDP or offers/questions pages
  setInitialSelectedVehicles() {
    const selectedOfferLists = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.offerLists;
    
    // if the previous search contained offer lists, pull the config value from the state so the button matches the search criteria
    if (selectedOfferLists?.length > 0) {
      const radioButtonSelectedValue = this.store.selectSnapshot(InventoryManagementState.accountManagerOfferListSelection);
      this.selectedValue.set(radioButtonSelectedValue);
    }
  }

  /** Check if "All Recipients" is selected */
  isAllRecipientsSelected(): boolean {
    return this.selectedValue() === 'all';
  }

  /** Check if "All Lists" for a specific account manager is selected */
  isAccountManagerAllListsSelected(accountManagerUuid: string): boolean {
    return this.selectedValue() === `accountManager:${accountManagerUuid}`;
  }

    /** Check if "All Lists" for a specific recipient is selected */
  isRecipientAllListsSelected(accountManagerUuid: string, dealerUuid: string, recipientUuid: string): boolean {
    return this.selectedValue() === `recipient:${accountManagerUuid}:${dealerUuid}:${recipientUuid}`;
  }

  /** Check if a specific offer list is selected */
  isOfferListSelected(offerListUuid: string): boolean {
    return this.selectedValue() === `offerList:${offerListUuid}`;
  }

  /** Select "All Recipients" */
  selectAllRecipients(): void {
    this.selectedValue.set('all');
    this.updateFilter();
  }

  /** Select "All Lists" for a specific account manager */
  selectAccountManagerAllLists(accountManagerUuid: string): void {
    this.selectedValue.set(`accountManager:${accountManagerUuid}`);
    this.updateFilter();
  }

    /** Select "All Lists" for a specific recipient */
  selectRecipientAllLists(accountManagerUuid: string, dealerUuid: string, recipientUuid: string): void {
    this.selectedValue.set(`recipient:${accountManagerUuid}:${dealerUuid}:${recipientUuid}`);
    this.updateFilter();
  }

  /** Select a specific offer list */
  selectOfferList(offerListUuid: string): void {
    this.selectedValue.set(`offerList:${offerListUuid}`);
    this.updateFilter();
  }

  /** Get visible dealers for an account manager (limited by visible count) */
  getVisibleDealers(accountManagerUuid: string, dealers: any[]): any[] {
    const visibleCountSignal = this.visibleDealerCounts[accountManagerUuid];
    const visibleCount = visibleCountSignal ? visibleCountSignal() : this.dealersPerPage;
    return dealers.slice(0, visibleCount);
  }

  /** Check if the toggle button should be shown */
  shouldShowDealerToggleButton(accountManagerUuid: string, dealers: any[]): boolean {
    return dealers.length > this.dealersPerPage;
  }

  /** Check if there are more dealers to show */
  hasMoreDealers(accountManagerUuid: string, dealers: any[]): boolean {
    const visibleCountSignal = this.visibleDealerCounts[accountManagerUuid];
    const visibleCount = visibleCountSignal ? visibleCountSignal() : this.dealersPerPage;
    return dealers.length > visibleCount;
  }

  /** Check if we can show less dealers (visible count > dealersPerPage) */
  canShowLessDealers(accountManagerUuid: string, dealers: any[]): boolean {
    const visibleCountSignal = this.visibleDealerCounts[accountManagerUuid];
    const visibleCount = visibleCountSignal ? visibleCountSignal() : this.dealersPerPage;
    return visibleCount > this.dealersPerPage && dealers.length >= this.dealersPerPage;
  }

  /** Show more dealers */
  showMoreDealers(event: MouseEvent, accountManagerUuid: string): void {
    event.stopPropagation();
    const visibleCountSignal = this.visibleDealerCounts[accountManagerUuid];
    if (visibleCountSignal) {
      visibleCountSignal.update(count => count + this.dealersPerPage);
    }
  }

  /** Show less dealers (reset to dealersPerPage) */
  showLessDealers(event: MouseEvent, accountManagerUuid: string): void {
    event.stopPropagation();
    const visibleCountSignal = this.visibleDealerCounts[accountManagerUuid];
    if (visibleCountSignal) {
      visibleCountSignal.set(this.dealersPerPage);
    }
  }

  /** Check if dealer dropdown is open */
  isDealerDropdownOpen(accountManagerUuid: string, dealerUuid: string): boolean {
    const key = `${accountManagerUuid}:${dealerUuid}`;
    return this.dealerDropdownOpen.get(key) ?? false;
  }

  /** Toggle dealer dropdown */
  toggleDealerDropdown(accountManagerUuid: string, dealerUuid: string): void {
    const key = `${accountManagerUuid}:${dealerUuid}`;
    const currentState = this.isDealerDropdownOpen(accountManagerUuid, dealerUuid);
    this.dealerDropdownOpen.set(key, !currentState);
  }

  /** Update the filter based on the current selection */
  private updateFilter(): void {
    const value = this.selectedValue();

    // update the state with the selected value
    this.store.dispatch(new UpdateInventoryManagementAccountManagerOfferListSelection(value));
    
    if (value === 'all') {
      // Clear all offer list filters
      this.searchFilterService.setOfferLists([]);
        } else if (value.startsWith('accountManager:')) {
      // Filter by account manager - get all offer lists for this account manager
      const accountManagerUuid = value.replace('accountManager:', '');
      const accountManager = this.accountManagersForOfferLists.find(am => am.value === accountManagerUuid);
      if (accountManager) {
        const offerListUuidsSet = new Set<string>();
        accountManager.buyerOeUuid.forEach(dealer => {
          dealer.userNameUuid.forEach(recipient => {
            recipient.offerListUuid.forEach(ol => offerListUuidsSet.add(ol.value));
          });
        });
        this.searchFilterService.setOfferLists([...offerListUuidsSet]);
      }
        } else if (value.startsWith('recipient:')) {
      // Filter by recipient - get all offer lists for this recipient
      const parts = value.replace('recipient:', '').split(':');
      const accountManagerUuid = parts[0];
      const dealerUuid = parts[1];
      const recipientUuid = parts[2];
      const accountManager = this.accountManagersForOfferLists.find(am => am.value === accountManagerUuid);
      if (accountManager) {
        const dealer = accountManager.buyerOeUuid.find(d => d.value === dealerUuid);
        if (dealer) {
          const recipient = dealer.userNameUuid.find(r => r.value === recipientUuid);
          if (recipient) {
            const offerListUuidsSet = new Set<string>(recipient.offerListUuid.map(ol => ol.value));
            this.searchFilterService.setOfferLists([...offerListUuidsSet]);
          }
        }
      }
    } else if (value.startsWith('offerList:')) {
      // Filter by specific offer list
      const offerListUuid = value.replace('offerList:', '');
      this.searchFilterService.setOfferLists([offerListUuid]);
    }
  }
}
